<template>
  <div class="hdrCheck" v-click-outside="close">
    <!-- Auslöser: Icon mit Soft-Badge (Anzahl offener Aufgaben) -->
    <button type="button" class="hdrCheck__trigger" :class="{ open }" @click="open = !open">
      <v-icon small>mdi-checkbox-marked-outline</v-icon>
      <span>Checkliste</span>
      <span v-if="openItems.length" class="hdrCheck__badge">{{ openItems.length }}</span>
    </button>

    <div v-if="open" class="hdrCheck__dropdown">
      <!-- Neue Aufgabe: Freitext (Order-Nr wird erkannt) + optionaler Assignee -->
      <div class="hdrCheck__addRow">
        <input
          v-model="newText"
          class="hdrCheck__input"
          type="text"
          placeholder="Neue Aufgabe… (Order-Nr wird erkannt, z. B. „#91022 Vorschau prüfen“)"
          :disabled="adding"
          @keydown.enter="addItem"
          @keydown.esc="close"
        />
        <select v-model="assignee" class="hdrCheck__assignee" title="Zuweisen an">
          <option :value="''">👥 Alle</option>
          <option v-for="u in assignableUsers" :key="u.uid" :value="u.uid">
            {{ u.username }}
          </option>
        </select>
        <button
          type="button"
          class="hdrCheck__addBtn"
          :disabled="adding || !newText.trim()"
          @click="addItem"
        >
          <v-progress-circular v-if="adding" indeterminate size="12" width="2" />
          <v-icon v-else small color="#fff">mdi-plus</v-icon>
        </button>
      </div>

      <!-- Offene Aufgaben -->
      <div v-if="!openItems.length" class="hdrCheck__empty">
        Keine offenen Aufgaben 🎉
      </div>
      <div v-for="item in openItems" :key="item.id" class="hdrCheck__row">
        <button type="button" class="hdrCheck__box" title="Abhaken" @click="toggleDone(item)">
          <v-icon x-small color="transparent">mdi-check</v-icon>
        </button>
        <span class="hdrCheck__text">
          <a v-if="item.orderUid" class="hdrCheck__order" @click.prevent="openOrder(item)">
            {{ item.orderName }}
          </a>
          {{ item.text }}
        </span>
        <span v-if="item.assignedTo" class="hdrCheck__chip">{{ item.assignedToName || "?" }}</span>
        <span class="hdrCheck__meta" :title="'von ' + (item.createdBy && item.createdBy.name)">
          {{ fromNow(item.createdAt) }}
        </span>
      </div>

      <!-- Erledigt: die letzten 10 -->
      <template v-if="doneItems.length">
        <div class="hdrCheck__divider"><span>Erledigt</span></div>
        <div v-for="item in doneItems" :key="item.id" class="hdrCheck__row done">
          <button type="button" class="hdrCheck__box checked" title="Wieder öffnen" @click="toggleDone(item)">
            <v-icon x-small color="#fff">mdi-check</v-icon>
          </button>
          <span class="hdrCheck__text">
            <a v-if="item.orderUid" class="hdrCheck__order" @click.prevent="openOrder(item)">
              {{ item.orderName }}
            </a>
            {{ item.text }}
          </span>
          <span class="hdrCheck__meta" :title="'erledigt von ' + (item.doneBy && item.doneBy.name)">
            ✓ {{ (item.doneBy && item.doneBy.name) || "" }} · {{ fromNow(item.doneAt) }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { collection, doc, limit, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { auth, db } from "../../fb";
import {
  addChecklistItem,
  detectOrderInChecklistText,
  getDateFromNow,
  openOrderModal,
} from "@/utils";

const ASSIGNEE_STORAGE_KEY = "td_checklist_default_assignee";

export default {
  name: "HeaderChecklist",
  data() {
    return {
      open: false,
      items: [],
      newText: "",
      adding: false,
      assignee: window.localStorage.getItem(ASSIGNEE_STORAGE_KEY) || "",
      unsubscribe: null,
    };
  },
  computed: {
    ...mapGetters(["allUsers"]),
    assignableUsers() {
      return (this.allUsers || []).filter((u) => u.username);
    },
    /** Sichtbarkeit: ohne Zuweisung sehen alle den Eintrag; mit Zuweisung nur
     *  der zugewiesene Nutzer und der Ersteller */
    visibleItems() {
      const me = auth.currentUser?.uid;
      return this.items.filter(
        (i) => !i.assignedTo || i.assignedTo === me || i.createdBy?.uid === me
      );
    },
    openItems() {
      return this.visibleItems
        .filter((i) => !i.done)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    },
    doneItems() {
      return this.visibleItems
        .filter((i) => i.done)
        .sort((a, b) => (b.doneAt || 0) - (a.doneAt || 0))
        .slice(0, 10);
    },
  },
  mounted() {
    const q = query(collection(db, "checklist"), orderBy("createdAt", "desc"), limit(300));
    this.unsubscribe = onSnapshot(q, (snap) => {
      this.items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    });
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    fromNow(ts) {
      return ts ? getDateFromNow(new Date(ts)) : "";
    },
    close() {
      this.open = false;
    },
    openOrder(item) {
      this.close();
      openOrderModal(item.orderUid);
    },
    async addItem() {
      const text = this.newText.trim();
      if (!text || this.adding) return;
      this.adding = true;
      try {
        // Order-Nr im Text? → verlinken und aus dem Anzeigetext ziehen
        const detected = await detectOrderInChecklistText(text);
        const assignedUser = this.assignableUsers.find((u) => u.uid === this.assignee);
        await addChecklistItem({
          text: detected ? detected.cleanText || text : text,
          orderUid: detected?.orderUid || null,
          orderName: detected?.orderName || null,
          assignedTo: assignedUser?.uid || null,
          assignedToName: assignedUser?.username || null,
        });
        // zuletzt gewählten Assignee als Default merken
        window.localStorage.setItem(ASSIGNEE_STORAGE_KEY, this.assignee || "");
        this.newText = "";
      } catch (e) {
        console.error("Checklist: Anlegen fehlgeschlagen", e);
      } finally {
        this.adding = false;
      }
    },
    async toggleDone(item) {
      const nowDone = !item.done;
      try {
        await updateDoc(doc(db, "checklist", item.id), {
          done: nowDone,
          doneAt: nowDone ? Date.now() : null,
          doneBy: nowDone
            ? { uid: auth.currentUser?.uid || null, name: auth.currentUser?.displayName || "" }
            : null,
        });
      } catch (e) {
        console.error("Checklist: Abhaken fehlgeschlagen", e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.hdrCheck {
  position: relative;
  margin-right: 12px;

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 14px;
    border-radius: 999px;
    background: #f4f3f7;
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);
    transition: background 0.15s, box-shadow 0.15s;

    &:hover,
    &.open {
      background: #edeafa;
      color: #6a4fc4;
    }
  }
  &__badge {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: #7b5cd6;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;
  }

  &__dropdown {
    position: absolute;
    top: 48px;
    right: 0;
    width: min(520px, 90vw);
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 8px 30px rgba(16, 24, 40, 0.18);
    padding: 10px;
    z-index: 20;
    max-height: 60vh;
    overflow-y: auto;
  }

  &__addRow {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }
  &__input {
    flex: 1;
    min-width: 0;
    height: 36px;
    padding: 0 12px;
    border-radius: 10px;
    background: #f4f3f7;
    border: none;
    outline: none;
    font-size: 13px;

    &:focus {
      background: #fff;
      box-shadow: 0 0 0 2px rgba(123, 92, 214, 0.35);
    }
  }
  &__assignee {
    height: 36px;
    max-width: 120px;
    padding: 0 6px;
    border-radius: 10px;
    background: #f4f3f7;
    border: none;
    outline: none;
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);
    cursor: pointer;
  }
  &__addBtn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #7b5cd6;
    flex-shrink: 0;

    &:disabled {
      opacity: 0.4;
    }
  }

  &__empty {
    padding: 14px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.5);
    text-align: center;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px;
    border-radius: 10px;

    &:hover {
      background: #faf9fc;
    }

    &.done .hdrCheck__text {
      text-decoration: line-through;
      color: rgba(0, 0, 0, 0.4);
    }
  }
  &__box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 6px;
    border: 2px solid #cfcadd;
    background: #fff;
    flex-shrink: 0;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
      border-color: #7b5cd6;
    }
    &.checked {
      background: #7b5cd6;
      border-color: #7b5cd6;
    }
  }
  &__text {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__order {
    font-weight: 700;
    color: #7b5cd6;
    cursor: pointer;
    margin-right: 4px;

    &:hover {
      text-decoration: underline;
    }
  }
  &__chip {
    padding: 1px 8px;
    border-radius: 999px;
    background: #edeafa;
    color: #6a4fc4;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
  }
  &__meta {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.4);
    white-space: nowrap;
  }

  &__divider {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0 4px;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.35);

    &::before,
    &::after {
      content: "";
      flex: 1;
      height: 1px;
      background: #eeecf4;
    }
  }
}
</style>
